import React from 'react';
import { cn } from '@shared/ui/utils/tailwind';

export type PaperProps = {
  children: React.ReactNode;
  padding?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  withBorder?: boolean;
  shadow?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  style?: React.CSSProperties;
};

const paddingMap: Record<string, string> = { xs: 'p-2', sm: 'p-3', md: 'p-4', lg: 'p-6', xl: 'p-8' };
const radiusMap: Record<string, string> = { xs: 'rounded-sm', sm: 'rounded', md: 'rounded-md', lg: 'rounded-lg', xl: 'rounded-xl' };
const shadowMap: Record<string, string> = { xs: 'shadow-sm', sm: 'shadow', md: 'shadow-md', lg: 'shadow-lg', xl: 'shadow-xl' };

function resolvePadding(padding: PaperProps['padding']): { className?: string; style?: React.CSSProperties } {
  if (typeof padding === 'number') {
    return { style: { padding: `${padding}px` } };
  }
  return { className: paddingMap[padding ?? 'md'] };
}

function resolveBorder(withBorder: boolean): string | undefined {
  return withBorder ? 'border border-[var(--border-medium)]' : undefined;
}

function resolveShadow(shadow: PaperProps['shadow']): string | undefined {
  return shadow ? shadowMap[shadow] : undefined;
}

export const Paper: React.FC<PaperProps> = ({
  children, padding = 'md', radius = 'md', withBorder = false, shadow, className, style,
}) => {
  const resolved = resolvePadding(padding);

  return (
    <div
      className={cn(
        'bg-[var(--surface-primary)]',
        resolved.className,
        radiusMap[radius as string],
        resolveBorder(withBorder),
        resolveShadow(shadow),
        className
      )}
      style={{ ...resolved.style, ...style }}
    >
      {children}
    </div>
  );
};

export default Paper;
